import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Bed,
  Check,
  X,
  Clock,
  Edit2,
  Users,
  Plus,
  Trash2,
  Loader2,
  Minus,
  Eye,
  UserSearch,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Calendar,
  PhoneCall,
  ThumbsUp,
  ThumbsDown,
  Home,
  UserPlus,
  Send,
  ListChecks,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { ScrollArea } from "../components/ui/scroll-area";
import PlanimetriaPanel from "../components/posti-liberi/PlanimetriaPanel";

/** Vista planimetria interattiva: disabilitata in UI finché la resa visiva non è pronta. */
const PLANIMETRIA_VIEW_ENABLED = false;

const API_URL = process.env.REACT_APP_BACKEND_URL;

function calcolaEta(dataNascita) {
  if (!dataNascita) return null;
  const d = new Date(dataNascita);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 && age < 130 ? age : null;
}

function OccupancyRing({ pct }) {
  const size = 44;
  const stroke = 3;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const c = 2 * Math.PI * r;
  const p = Math.min(100, Math.max(0, pct));
  const filled = (p / 100) * c;
  return (
    <svg width={size} height={size} className="shrink-0 -rotate-90">
      <circle cx={cx} cy={cy} r={r} fill="none" strokeWidth={stroke} className="text-slate-100" stroke="currentColor" />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        strokeWidth={stroke}
        strokeDasharray={`${filled} ${c}`}
        strokeLinecap="round"
        className="text-emerald-500"
        stroke="currentColor"
      />
    </svg>
  );
}

function KpiPostiInline({ variant, icon: Icon, value, label, testId }) {
  const isAvailable = variant === "available";
  return (
    <div
      className={`flex items-center gap-3 min-w-0 rounded-xl px-4 py-3 ring-1 ${
        isAvailable
          ? "bg-emerald-50/90 ring-emerald-100"
          : "bg-rose-50/90 ring-rose-100"
      }`}
      data-testid={testId}
    >
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
          isAvailable ? "bg-emerald-100" : "bg-rose-100"
        }`}
      >
        <Icon
          className={`w-5 h-5 ${isAvailable ? "text-emerald-600" : "text-rose-600"}`}
          strokeWidth={2}
        />
      </div>
      <p
        className={`text-3xl font-bold tabular-nums leading-none tracking-tight shrink-0 ${
          isAvailable ? "text-emerald-600" : "text-rose-600"
        }`}
      >
        {value}
      </p>
      <p className="text-sm text-slate-600 whitespace-nowrap">{label}</p>
    </div>
  );
}

function PostiKpiBar({ postiDisponibili, postiOccupati, onAddRoom }) {
  return (
    <Card className="card-base mb-6 shadow-sm border border-slate-200/80 bg-white">
      <CardContent className="p-4 md:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8 lg:gap-12 min-w-0 flex-1">
            <KpiPostiInline
              variant="available"
              icon={Bed}
              value={postiDisponibili}
              label="Posti disponibili"
              testId="stat-posti-disponibili"
            />
            <KpiPostiInline
              variant="occupied"
              icon={Users}
              value={postiOccupati}
              label="Posti occupati"
              testId="stat-posti-occupati"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={onAddRoom}
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 shrink-0 w-full sm:w-auto px-6"
            data-testid="add-room"
          >
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi camera
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RichiestaOspiteCard({ onInvia }) {
  return (
    <Card className="card-base h-full shadow-sm border border-slate-200/80 bg-white">
      <CardContent className="p-4 md:p-5 h-full">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 h-full">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <UserPlus className="w-5 h-5 text-emerald-600" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="text-base font-bold text-slate-900">Posto libero?</p>
              <p className="text-sm text-slate-500 mt-0.5">Invia richiesta a ospite o referente.</p>
            </div>
          </div>
          <Button
            type="button"
            className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 w-full sm:w-auto px-6"
            onClick={onInvia}
            data-testid="btn-invia-richiesta-ospite"
          >
            <Send className="w-4 h-4 mr-2" />
            Invia richiesta ospite
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PostiLiberiViewSelector({ value, onChange, planimetriaEnabled = false }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className="h-12 w-auto min-w-[220px] gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-base shadow-sm hover:bg-slate-50/80 focus:ring-emerald-500/25 [&>span]:flex [&>span]:items-center [&>span]:gap-3 [&>svg:last-child]:h-5 [&>svg:last-child]:w-5 [&>svg:last-child]:ml-1 [&>svg:last-child]:text-slate-400"
        data-testid="view-mode-select"
        aria-label="Seleziona vista"
      >
        <span className="flex items-center gap-3 shrink-0" data-testid="view-mode-selector">
          <Eye className="w-5 h-5 text-emerald-600 shrink-0" strokeWidth={2} />
          <span className="text-base text-slate-500 font-normal">Vista:</span>
        </span>
        <SelectValue className="text-base font-semibold text-slate-800 pr-1" />
      </SelectTrigger>
      <SelectContent align="end" className="min-w-[220px] rounded-xl">
        <SelectItem value="simple" className="py-2.5 text-base" data-testid="view-simple">
          Semplice
        </SelectItem>
        <SelectItem value="complete" className="py-2.5 text-base" data-testid="view-complete">
          Completa
        </SelectItem>
        {planimetriaEnabled && (
          <SelectItem value="planimetria" className="py-2.5 text-base" data-testid="view-planimetria">
            Planimetria
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

function RichiestaOspiteRow({ onInvia, viewMode, onViewModeChange, planimetriaEnabled = false }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 mb-6 w-full">
      <div className="w-full md:w-[80%] md:max-w-[80%] min-w-0">
        <RichiestaOspiteCard onInvia={onInvia} />
      </div>
      <div className="flex shrink-0 items-center md:pl-2 md:pr-1">
        <PostiLiberiViewSelector
          value={viewMode}
          onChange={onViewModeChange}
          planimetriaEnabled={planimetriaEnabled}
        />
      </div>
    </div>
  );
}

export default function PostiLiberi() {
  const { token, activeStruttura } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isNewRoom, setIsNewRoom] = useState(false);
  const [bulkRoomsOpen, setBulkRoomsOpen] = useState(false);
  const [bulkRoomsSaving, setBulkRoomsSaving] = useState(false);
  const [resetAssignmentsOpen, setResetAssignmentsOpen] = useState(false);
  const [bulkRoomsForm, setBulkRoomsForm] = useState({
    prefisso: "Camera ", numero_iniziale: 1, quantita: 1, tipo: "Doppia", piano: "", note: "",
    sostituisci_configurazione_esistente: false,
  });
  
  // Toggle per modalità visualizzazione
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("postiLiberiViewMode") || "simple";
    if (saved === "planimetria" && !PLANIMETRIA_VIEW_ENABLED) return "complete";
    return saved;
  });
  
  // Stato per quick update
  const [quickPosti, setQuickPosti] = useState(0);
  const [quickSaving, setQuickSaving] = useState(false);
  
  // Stato per Lead Compatibili
  const [leadCompatibili, setLeadCompatibili] = useState([]);
  const [leadCount, setLeadCount] = useState(0);
  const [loadingLead, setLoadingLead] = useState(false);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetailOpen, setLeadDetailOpen] = useState(false);
  const [updatingStato, setUpdatingStato] = useState(false);
  

  const [assignOspiteOpen, setAssignOspiteOpen] = useState(false);
  const [assignTargetRoom, setAssignTargetRoom] = useState(null);
  const [fascicoliSenzaCamera, setFascicoliSenzaCamera] = useState([]);
  const [loadingFascicoliAssign, setLoadingFascicoliAssign] = useState(false);
  const [selectedFascicoloId, setSelectedFascicoloId] = useState("");
  const [assigningOspite, setAssigningOspite] = useState(false);

  const [richiestaOspiteFormOpen, setRichiestaOspiteFormOpen] = useState(false);
  const [richiestaLeadContext, setRichiestaLeadContext] = useState(null);
  const [richiestaForm, setRichiestaForm] = useState({
    data_ingresso_prevista: "",
    sesso: "",
    tipologia: "",
    camera: "",
    note: "",
  });
  const [invioRichiestaLoading, setInvioRichiestaLoading] = useState(false);
  const [richiestaInviataDialogOpen, setRichiestaInviataDialogOpen] = useState(false);
  const [richiesteOspite, setRichiesteOspite] = useState([]);
  const [richiesteOspiteDialogOpen, setRichiesteOspiteDialogOpen] = useState(false);
  const [loadingRichiesteOspite, setLoadingRichiesteOspite] = useState(false);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    fetchData();
    fetchLeadCompatibili();
    fetchRichiesteOspite();
  }, [token, activeStruttura?.id]);

  const fetchRichiesteOspite = async () => {
    if (!token) return;
    setLoadingRichiesteOspite(true);
    try {
      const response = await axios.get(`${API_URL}/api/posti-liberi/richieste-ospite`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRichiesteOspite(response.data.richieste || []);
    } catch (error) {
      console.error("Fetch richieste ospite error:", error);
    } finally {
      setLoadingRichiesteOspite(false);
    }
  };

  const openRichiestaOspiteForm = (lead = null) => {
    setRichiestaLeadContext(lead);
    if (lead) {
      const extra = [
        lead.nome_familiare && `Familiare/contatto: ${lead.nome_familiare}`,
        (lead.nome_ospite || lead.cognome_ospite) && `Ospite: ${[lead.nome_ospite, lead.cognome_ospite].filter(Boolean).join(" ")}`,
        lead.telefono && `Tel. ${lead.telefono}`,
        lead.note_contatto && `Note lead: ${lead.note_contatto}`,
      ].filter(Boolean).join("\n");
      setRichiestaForm({ data_ingresso_prevista: "", sesso: "", tipologia: "", camera: "", note: extra });
    } else {
      setRichiestaForm({ data_ingresso_prevista: "", sesso: "", tipologia: "", camera: "", note: "" });
    }
    setRichiestaOspiteFormOpen(true);
  };

  const submitRichiestaOspite = async () => {
    if (
      !richiestaForm.data_ingresso_prevista ||
      !richiestaForm.sesso ||
      !richiestaForm.tipologia ||
      !richiestaForm.camera
    ) {
      toast.error("Compila data di ingresso, sesso, tipologia e tipo di camera");
      return;
    }
    setInvioRichiestaLoading(true);
    try {
      const body = {
        data_ingresso_prevista: richiestaForm.data_ingresso_prevista,
        sesso: richiestaForm.sesso,
        tipologia: richiestaForm.tipologia,
        camera: richiestaForm.camera,
        note: richiestaForm.note,
      };
      if (richiestaLeadContext) {
        body.lead_compatibile_id = richiestaLeadContext.id;
        body.lead_dettaglio = {
          nome_familiare: richiestaLeadContext.nome_familiare,
          nome_ospite: richiestaLeadContext.nome_ospite,
          cognome_ospite: richiestaLeadContext.cognome_ospite,
          telefono: richiestaLeadContext.telefono,
          email: richiestaLeadContext.email,
          citta: richiestaLeadContext.citta,
          note_contatto: richiestaLeadContext.note_contatto,
        };
      }
      await axios.post(`${API_URL}/api/posti-liberi/richiesta-ospite`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRichiestaOspiteFormOpen(false);
      setRichiestaLeadContext(null);
      setRichiestaForm({ data_ingresso_prevista: "", sesso: "", tipologia: "", camera: "", note: "" });
      setRichiestaInviataDialogOpen(true);
      fetchRichiesteOspite();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore nell'invio della richiesta");
    } finally {
      setInvioRichiestaLoading(false);
    }
  };

  const nascondiRichiestaOspite = async (id) => {
    try {
      await axios.post(`${API_URL}/api/posti-liberi/richieste-ospite/${id}/nascondi`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Richiesta rimossa dal tuo elenco");
      fetchRichiesteOspite();
    } catch (error) {
      toast.error("Impossibile aggiornare la richiesta");
    }
  };

  const getStatoRichiestaOspiteBadge = (stato) => {
    const s = stato || "in attesa";
    if (s === "completata") return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (s === "in lavorazione") return "bg-amber-100 text-amber-800 border-amber-200";
    return "bg-slate-100 text-slate-700 border-slate-200";
  };

  const formatStatoRichiestaLabel = (stato) => {
    const s = stato || "in attesa";
    if (s === "in lavorazione") return "In lavorazione";
    if (s === "completata") return "Completata";
    return "In attesa";
  };
  
  useEffect(() => {
    if (viewMode === "planimetria" && !PLANIMETRIA_VIEW_ENABLED) {
      setViewMode("complete");
      return;
    }
    localStorage.setItem("postiLiberiViewMode", viewMode);
  }, [viewMode]);
  
  useEffect(() => {
    if (data?.posti_disponibili !== undefined) {
      setQuickPosti(data.posti_disponibili);
    }
  }, [data?.posti_disponibili]);

  const roomsList = data?.rooms ?? [];
  const totalPages = Math.max(1, Math.ceil(roomsList.length / pageSize));
  const pagedRooms = useMemo(() => {
    const start = (page - 1) * pageSize;
    return roomsList.slice(start, start + pageSize);
  }, [roomsList, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [roomsList.length, viewMode]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posti-liberi`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchLeadCompatibili = async () => {
    setLoadingLead(true);
    try {
      const response = await axios.get(`${API_URL}/api/posti-liberi/lead-compatibili`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadCompatibili(response.data.leads || []);
      setLeadCount(response.data.count || 0);
    } catch (error) {
      console.error("Fetch lead error:", error);
    } finally {
      setLoadingLead(false);
    }
  };
  
  
  const handleUpdateStato = async (nuovoStato) => {
    if (!selectedLead) return;
    
    setUpdatingStato(true);
    try {
      await axios.post(
        `${API_URL}/api/posti-liberi/lead-compatibili/${selectedLead.id}/stato`,
        { lead_id: selectedLead.id, nuovo_stato: nuovoStato },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Stato aggiornato: ${nuovoStato}`);
      
      // Aggiorna lo stato locale
      setSelectedLead(prev => ({ ...prev, stato_interazione: nuovoStato }));
      setLeadCompatibili(prev => 
        prev.map(l => l.id === selectedLead.id ? { ...l, stato_interazione: nuovoStato } : l)
      );
    } catch (error) {
      console.error("Update stato error:", error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setUpdatingStato(false);
    }
  };
  
  const getStatoBadgeColor = (stato) => {
    switch (stato) {
      case "Visualizzato dalla struttura": return "bg-blue-100 text-blue-700 border-blue-200";
      case "Contatto avviato": return "bg-amber-100 text-amber-700 border-amber-200";
      case "Interessata": return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "Non idoneo": return "bg-red-100 text-red-700 border-red-200";
      case "Ingresso confermato": return "bg-purple-100 text-purple-700 border-purple-200";
      default: return "bg-slate-100 text-slate-600 border-slate-200";
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    } catch {
      return dateString;
    }
  };
  
  // Auto-set "Visualizzato dalla struttura" quando si apre il dettaglio
  const handleLeadClick = async (lead) => {
    setSelectedLead(lead);
    setLeadDetailOpen(true);
    
    // Se non ha ancora uno stato, imposta automaticamente "Visualizzato dalla struttura"
    if (!lead.stato_interazione) {
      try {
        await axios.post(
          `${API_URL}/api/posti-liberi/lead-compatibili/${lead.id}/stato`,
          { lead_id: lead.id, nuovo_stato: "Visualizzato dalla struttura" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Aggiorna lo stato locale
        const nuovoStato = "Visualizzato dalla struttura";
        setSelectedLead(prev => ({ ...prev, stato_interazione: nuovoStato }));
        setLeadCompatibili(prev => 
          prev.map(l => l.id === lead.id ? { ...l, stato_interazione: nuovoStato } : l)
        );
      } catch (error) {
        console.error("Errore auto-update stato:", error);
      }
    }
  };

  // Quick update per modalità semplice
  const handleQuickUpdate = async (newPosti) => {
    if (newPosti < 0) return;
    
    setQuickSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/posti-liberi/quick-update`,
        { posti_disponibili: newPosti },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuickPosti(newPosti);
      setData(prev => ({ ...prev, posti_disponibili: newPosti }));
      toast.success(`Posti aggiornati: ${newPosti}`);
    } catch (error) {
      console.error("Quick update error:", error);
      toast.error("Errore nell'aggiornamento");
    } finally {
      setQuickSaving(false);
    }
  };

  const handleAddRoom = () => {
    setSelectedRoom({
      name: "",
      type: "Singola",
      posti_liberi: 1,
      notes: "",
      piano: "",
    });
    setIsNewRoom(true);
    setModalOpen(true);
  };

  const handleUpdateClick = (room) => {
    setSelectedRoom({ ...room });
    setIsNewRoom(false);
    setModalOpen(true);
  };

  const handleDeleteClick = (room) => {
    setSelectedRoom(room);
    setDeleteModalOpen(true);
  };

  const handleSaveRoom = async () => {
    if (!selectedRoom.name) {
      toast.error("Inserisci il nome della camera");
      return;
    }

    setSaving(true);
    try {
      if (isNewRoom) {
        // Crea nuova camera
        await axios.post(`${API_URL}/api/posti-liberi/camere`, {
          name: selectedRoom.name,
          type: selectedRoom.type,
          posti_liberi: selectedRoom.posti_liberi,
          notes: selectedRoom.notes,
          piano: selectedRoom.piano || "",
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Camera aggiunta!");
      } else {
        // Aggiorna camera esistente
        await axios.put(`${API_URL}/api/posti-liberi/camere/${selectedRoom.id}`, {
          name: selectedRoom.name,
          type: selectedRoom.type,
          posti_liberi: selectedRoom.posti_liberi,
          notes: selectedRoom.notes,
          piano: selectedRoom.piano || "",
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Camera aggiornata!");
      }
      
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRoom = async () => {
    try {
      await axios.delete(`${API_URL}/api/posti-liberi/camere/${selectedRoom.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Camera rimossa!");
      setDeleteModalOpen(false);
      fetchData();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Errore nella rimozione");
    }
  };

  const handleCreateRoomsBulk = async () => {
    setBulkRoomsSaving(true);
    try {
      const { data: result } = await axios.post(
        `${API_URL}/api/posti-liberi/camere/bulk`,
        { ...bulkRoomsForm, numero_iniziale: Number(bulkRoomsForm.numero_iniziale), quantita: Number(bulkRoomsForm.quantita) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${result.created} camere configurate`);
      setBulkRoomsOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile configurare le camere");
    } finally {
      setBulkRoomsSaving(false);
    }
  };

  const handleResetAssignments = async () => {
    setBulkRoomsSaving(true);
    try {
      const { data: result } = await axios.post(
        `${API_URL}/api/posti-liberi/camere/reset-assegnazioni`, {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${result.assegnazioni_rimosse} associazioni camera rimosse`);
      setResetAssignmentsOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile azzerare le assegnazioni");
    } finally {
      setBulkRoomsSaving(false);
    }
  };

  const openAssignOspiteDialog = async (room) => {
    setAssignTargetRoom(room);
    setSelectedFascicoloId("");
    setAssignOspiteOpen(true);
    setLoadingFascicoliAssign(true);
    try {
      const res = await axios.get(`${API_URL}/api/fascicoli-assistenziali`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = (res.data || []).filter((f) => !f.archiviata && !f.room_id);
      list.sort((a, b) =>
        `${a.cognome || ""} ${a.nome || ""}`.localeCompare(
          `${b.cognome || ""} ${b.nome || ""}`,
          "it",
          { sensitivity: "base" }
        )
      );
      setFascicoliSenzaCamera(list);
    } catch (error) {
      console.error("Fascicoli assign fetch error:", error);
      toast.error(
        error.response?.status === 403
          ? "Non hai permesso di consultare i fascicoli per assegnare un ospite"
          : "Impossibile caricare l'elenco ospiti"
      );
      setFascicoliSenzaCamera([]);
    } finally {
      setLoadingFascicoliAssign(false);
    }
  };

  const handleAssignOspiteToRoom = async () => {
    if (!assignTargetRoom?.id || !selectedFascicoloId) {
      toast.error("Seleziona un ospite");
      return;
    }
    setAssigningOspite(true);
    try {
      await axios.put(
        `${API_URL}/api/fascicoli-assistenziali/${selectedFascicoloId}`,
        { room_id: assignTargetRoom.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Ospite assegnato alla camera");
      setAssignOspiteOpen(false);
      setAssignTargetRoom(null);
      setSelectedFascicoloId("");
      fetchData();
    } catch (error) {
      console.error("Assign ospite error:", error);
      toast.error(error.response?.data?.detail || "Errore nell'assegnazione");
    } finally {
      setAssigningOspite(false);
    }
  };

  // Calcola posti max in base al tipo camera
  const getMaxPosti = (type) => (type === "Singola" ? 1 : 2);

  const totPostiLetto = data?.totale_posti_letto || 0;
  const postiDisponibili = data?.posti_disponibili ?? 0;
  const postiOccupati = Math.max(0, totPostiLetto - postiDisponibili);

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 w-full min-w-0">
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-slate-200 rounded-xl" />
          <div className="h-64 bg-slate-200 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 w-full min-w-0 max-w-none bg-slate-50/60 min-h-[calc(100vh-4rem)]">
      <PageHeader
        title="Camere e posti letto"
        subtitle="Gestisci disponibilità camere e posti letto"
        icon={Bed}
        iconContainerClassName="bg-gradient-to-br from-emerald-500 to-emerald-600"
        iconClassName="text-white"
      />
      {viewMode === "complete" && (
        <div className="mb-5 flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setBulkRoomsOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />Configura più camere
          </Button>
          <Button variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => setResetAssignmentsOpen(true)}>
            <X className="w-4 h-4 mr-2" />Azzera assegnazioni
          </Button>
        </div>
      )}

      {/* MODALITÀ SEMPLICE */}
      {viewMode === "simple" && (
        <div className="flex flex-col items-center justify-center py-8">
          {/* Barra Lead Compatibili - Modalità Semplice */}
          {leadCount > 0 && (
            <Card 
              className="card-base w-full max-w-md mb-6 border-l-4 border-l-amber-500 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setLeadModalOpen(true)}
              data-testid="lead-compatibili-bar-simple"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <UserSearch className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Richieste compatibili</p>
                      <p className="text-xs text-slate-500">Lead selezionati dal team Casadiriposoperte.it</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-500 text-white font-bold px-3 py-1">
                      {leadCount}
                    </Badge>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="w-full">
            <RichiestaOspiteRow
              onInvia={() => openRichiestaOspiteForm(null)}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              planimetriaEnabled={PLANIMETRIA_VIEW_ENABLED}
            />
          </div>
          
          <Card className="card-base w-full max-w-md bg-gradient-to-br from-emerald-500 to-emerald-600 text-white" data-testid="simple-view-card">
            <CardContent className="p-8 text-center">
              <p className="text-emerald-100 text-lg mb-2">Posti Liberi</p>
              <p className="text-8xl font-bold mb-4">{quickPosti}</p>
              
              <div className="flex items-center justify-center gap-4 mt-6">
                <Button
                  onClick={() => handleQuickUpdate(quickPosti - 1)}
                  disabled={quickPosti <= 0 || quickSaving}
                  className="w-16 h-16 rounded-full bg-white/20 hover:bg-white/30 text-white text-2xl font-bold"
                  data-testid="quick-minus"
                >
                  {quickSaving ? <Loader2 className="w-6 h-6 animate-spin" /> : <Minus className="w-8 h-8" />}
                </Button>
                
                <div className="w-24 text-center">
                  <Input
                    type="number"
                    min="0"
                    value={quickPosti}
                    onChange={(e) => setQuickPosti(Math.max(0, parseInt(e.target.value) || 0))}
                    onBlur={() => handleQuickUpdate(quickPosti)}
                    className="text-center text-2xl font-bold h-14 bg-white/20 border-white/30 text-white placeholder-white/50"
                    data-testid="quick-input"
                  />
                </div>
                
                <Button
                  onClick={() => handleQuickUpdate(quickPosti + 1)}
                  disabled={quickSaving}
                  className="w-16 h-16 rounded-full bg-white/20 hover:bg-white/30 text-white text-2xl font-bold"
                  data-testid="quick-plus"
                >
                  {quickSaving ? <Loader2 className="w-6 h-6 animate-spin" /> : <Plus className="w-8 h-8" />}
                </Button>
              </div>
              
              <p className="text-emerald-100 text-sm mt-6">
                Usa + e - per aggiornare rapidamente i posti disponibili
              </p>
            </CardContent>
          </Card>
          
          <p className="text-slate-500 text-sm mt-4">
            Passa alla modalità <button onClick={() => setViewMode("complete")} className="text-emerald-600 font-medium underline">Completa</button> per gestire le singole camere
          </p>
        </div>
      )}

      {/* MODALITÀ COMPLETA */}
      {viewMode === "complete" && (
        <>
          {leadCount > 0 && (
            <Card
              className="card-base mb-6 border-l-4 border-l-amber-500 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setLeadModalOpen(true)}
              data-testid="lead-compatibili-bar-complete"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                      <UserSearch className="w-6 h-6 text-amber-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">Richieste compatibili con la tua struttura</p>
                      <p className="text-sm text-slate-500">Lead selezionati dal team Casadiriposoperte.it</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className="bg-amber-500 text-white font-bold px-3 py-1.5">
                      {leadCount}
                    </Badge>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <RichiestaOspiteRow
            onInvia={() => openRichiestaOspiteForm(null)}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            planimetriaEnabled={PLANIMETRIA_VIEW_ENABLED}
          />

          <PostiKpiBar
            postiDisponibili={postiDisponibili}
            postiOccupati={postiOccupati}
            onAddRoom={handleAddRoom}
          />

          <Card className="card-base bg-white shadow-sm" data-testid="rooms-table-card">
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-lg">Elenco Camere</CardTitle>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Clock className="w-4 h-4 shrink-0" />
                  <span>Aggiornato {data?.last_updated}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto -mx-1">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Camera</TableHead>
                      <TableHead>Tipologia</TableHead>
                      <TableHead className="whitespace-nowrap">Posti letto</TableHead>
                      <TableHead className="whitespace-nowrap">Occupazione</TableHead>
                      <TableHead className="min-w-[180px]">Occupanti</TableHead>
                      <TableHead className="hidden lg:table-cell">Note</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedRooms.map((room) => {
                      const maxP = getMaxPosti(room.type);
                      const occupati = Math.max(0, maxP - room.posti_liberi);
                      const pctOcc = maxP ? Math.round((occupati / maxP) * 100) : 0;
                      let occLabel = "Libera";
                      if (occupati === maxP && maxP > 0) occLabel = `${occupati} occupat${occupati === 1 ? "o" : "i"}`;
                      else if (occupati > 0) occLabel = `${occupati} occupato`;
                      return (
                        <TableRow key={room.id} className="table-row-hover" data-testid={`room-${room.id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900">{room.name}</p>
                              {(room.piano || "").toString().trim() ? (
                                <p className="text-xs text-slate-500">{room.piano}</p>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className={
                                room.type === "Singola"
                                  ? "bg-sky-100 text-sky-800 border-sky-200"
                                  : "bg-violet-100 text-violet-800 border-violet-200"
                              }
                            >
                              {room.type === "Singola" ? "Singola" : "Doppia"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-0.5">
                                {Array.from({ length: maxP }).map((_, i) => (
                                  <Bed
                                    key={i}
                                    className={`w-5 h-5 shrink-0 ${
                                      i < occupati ? "text-red-500" : "text-emerald-500"
                                    }`}
                                    strokeWidth={2}
                                  />
                                ))}
                              </div>
                              <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
                                {occupati} / {maxP}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <OccupancyRing pct={pctOcc} />
                              <div className="text-xs leading-tight text-slate-600">
                                <span className="font-semibold tabular-nums">{pctOcc}%</span>
                                <br />
                                <span className="text-slate-500">{occLabel}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {room.ospiti && room.ospiti.length > 0 ? (
                              <div className="flex flex-col gap-2">
                                {room.ospiti.map((ospite) => {
                                  const eta = calcolaEta(ospite.data_nascita);
                                  const label =
                                    [ospite.cognome, ospite.nome].filter(Boolean).join(" ") ||
                                    ospite.nome_completo ||
                                    "Ospite";
                                  const initials =
                                    `${(ospite.cognome || "").charAt(0) || ""}${(ospite.nome || "").charAt(0) || ""}`.toUpperCase() ||
                                    "?";
                                  return (
                                    <div key={ospite.id} className="flex items-center gap-2 min-w-0">
                                      {ospite.foto_url ? (
                                        <img
                                          src={ospite.foto_url}
                                          alt=""
                                          className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0"
                                        />
                                      ) : (
                                        <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600 shrink-0">
                                          {initials}
                                        </div>
                                      )}
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-800 truncate">{label}</p>
                                        {eta != null ? (
                                          <p className="text-xs text-slate-500">{eta} anni</p>
                                        ) : null}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-slate-600 max-w-[220px] text-sm">
                            <span className="line-clamp-2">{room.notes || "—"}</span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {room.posti_liberi > 0 ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                                  title="Assegna ospite"
                                  onClick={() => openAssignOspiteDialog(room)}
                                  data-testid={`assign-ospite-${room.id}`}
                                >
                                  <UserPlus className="w-4 h-4" />
                                </Button>
                              ) : null}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUpdateClick(room)}
                                data-testid={`edit-${room.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(room)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-${room.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 mt-2 border-t border-slate-100 px-1">
                <p className="text-sm text-slate-600">
                  Mostra <span className="font-semibold tabular-nums">{pageSize}</span> elementi per pagina
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Precedente
                  </Button>
                  <span className="text-sm text-slate-600 px-2 tabular-nums">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Successiva
                  </Button>
                </div>
                <p className="text-sm text-slate-500 tabular-nums">
                  {roomsList.length === 0
                    ? "0 di 0 elementi"
                    : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, roomsList.length)} di ${roomsList.length} elementi`}
                </p>
              </div>
            </CardContent>
          </Card>

        </>
      )}

      {PLANIMETRIA_VIEW_ENABLED && viewMode === "planimetria" && (
        <>
          <RichiestaOspiteRow
            onInvia={() => openRichiestaOspiteForm(null)}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            planimetriaEnabled={PLANIMETRIA_VIEW_ENABLED}
          />
          <PlanimetriaPanel
            token={token}
            rooms={roomsList}
            onAssignOspite={openAssignOspiteDialog}
            onRefresh={fetchData}
          />
        </>
      )}

      {/* Assegna ospite a camera */}
      <Dialog
        open={assignOspiteOpen}
        onOpenChange={(open) => {
          setAssignOspiteOpen(open);
          if (!open) {
            setAssignTargetRoom(null);
            setSelectedFascicoloId("");
            setFascicoliSenzaCamera([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Assegna ospite
            </DialogTitle>
            <DialogDescription>
              Collega un ospite senza camera al fascicolo assistenziale alla camera{" "}
              <strong>{assignTargetRoom?.name}</strong>
              {assignTargetRoom?.posti_liberi > 0 ? (
                <span className="text-slate-600">
                  {" "}
                  ({assignTargetRoom.posti_liberi} posto
                  {assignTargetRoom.posti_liberi === 1 ? "" : "i"} libero
                  {assignTargetRoom.posti_liberi === 1 ? "" : "i"})
                </span>
              ) : null}
              .
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {loadingFascicoliAssign ? (
              <div className="flex items-center justify-center gap-2 py-8 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Caricamento ospiti…</span>
              </div>
            ) : fascicoliSenzaCamera.length === 0 ? (
              <p className="text-sm text-slate-600 py-4 text-center">
                Non ci sono ospiti con fascicolo senza camera. Crea o aggiorna un fascicolo da{" "}
                <span className="font-medium">Fascicoli assistenziali</span> se l&apos;ospite non ha ancora una camera
                assegnata.
              </p>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="fascicolo-assign-select">Ospite (fascicolo)</Label>
                <Select value={selectedFascicoloId} onValueChange={setSelectedFascicoloId}>
                  <SelectTrigger id="fascicolo-assign-select" data-testid="select-fascicolo-assign">
                    <SelectValue placeholder="Seleziona un ospite…" />
                  </SelectTrigger>
                  <SelectContent>
                    {fascicoliSenzaCamera.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {[f.cognome, f.nome].filter(Boolean).join(" ") || f.nome_completo}
                        {f.data_nascita
                          ? ` · ${formatDate(f.data_nascita)}`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setAssignOspiteOpen(false)}
            >
              Annulla
            </Button>
            <Button
              type="button"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={
                assigningOspite ||
                !selectedFascicoloId ||
                fascicoliSenzaCamera.length === 0 ||
                loadingFascicoliAssign
              }
              onClick={handleAssignOspiteToRoom}
              data-testid="confirm-assign-ospite"
            >
              {assigningOspite ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assegnazione…
                </>
              ) : (
                "Assegna alla camera"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit/Add Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{isNewRoom ? 'Aggiungi camera' : 'Modifica camera'}</DialogTitle>
            <DialogDescription>
              {isNewRoom
                ? "Aggiungi una nuova camera alla struttura"
                : `Modifica i dati della ${selectedRoom?.name}`}
            </DialogDescription>
          </DialogHeader>
          {selectedRoom && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome/Numero Camera *</Label>
                  <Input 
                    value={selectedRoom.name} 
                    onChange={(e) => setSelectedRoom({ ...selectedRoom, name: e.target.value })}
                    placeholder="Es. Camera 101"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo camera *</Label>
                  <Select 
                    value={selectedRoom.type} 
                    onValueChange={(v) => setSelectedRoom({ 
                      ...selectedRoom, 
                      type: v,
                      posti_liberi: Math.min(selectedRoom.posti_liberi, v === "Singola" ? 1 : 2)
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Singola">Singola (1 posto)</SelectItem>
                      <SelectItem value="Doppia">Doppia (2 posti)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Posti liberi</Label>
                <div className="flex gap-2">
                  {Array.from({ length: getMaxPosti(selectedRoom.type) + 1 }, (_, i) => i).map((num) => (
                    <Button
                      key={num}
                      type="button"
                      variant={selectedRoom.posti_liberi === num ? "default" : "outline"}
                      className={`flex-1 ${
                        selectedRoom.posti_liberi === num
                          ? num === 0 
                            ? "bg-red-500 hover:bg-red-600" 
                            : "bg-emerald-500 hover:bg-emerald-600"
                          : ""
                      }`}
                      onClick={() => setSelectedRoom({ ...selectedRoom, posti_liberi: num })}
                    >
                      {num === 0 ? (
                        <>
                          <X className="w-4 h-4 mr-1" />
                          Occupata
                        </>
                      ) : num === getMaxPosti(selectedRoom.type) ? (
                        <>
                          <Check className="w-4 h-4 mr-1" />
                          {num} {num === 1 ? "Libero" : "Liberi"}
                        </>
                      ) : (
                        <>
                          <Users className="w-4 h-4 mr-1" />
                          {num} Libero
                        </>
                      )}
                    </Button>
                  ))}
                </div>
                {selectedRoom.type === "Doppia" && (
                  <p className="text-xs text-slate-500 mt-1">
                    Per le camere doppie puoi specificare se sono disponibili 0, 1 o 2 posti
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Note (opzionale)</Label>
                <Input
                  value={selectedRoom.notes || ""}
                  onChange={(e) => setSelectedRoom({ ...selectedRoom, notes: e.target.value })}
                  placeholder="Es. Bagno privato, note interne…"
                />
              </div>

              <div className="space-y-2">
                <Label>Piano (opzionale)</Label>
                <Input
                  value={selectedRoom.piano ?? ""}
                  onChange={(e) => setSelectedRoom({ ...selectedRoom, piano: e.target.value })}
                  placeholder="Es. Piano 1"
                />
              </div>

              <div className="bg-slate-50 rounded-lg p-3 mt-4">
                <p className="text-sm text-slate-600">
                  <strong>Contributo ai posti totali:</strong>{" "}
                  <span className={selectedRoom.posti_liberi > 0 ? "text-emerald-600" : "text-slate-400"}>
                    +{selectedRoom.posti_liberi} posti
                  </span>
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleSaveRoom}
              disabled={saving}
              className="bg-[#4CAF50] hover:bg-[#43A047]"
              data-testid="save-room-update"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                "Salva"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Conferma rimozione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler rimuovere la <strong>{selectedRoom?.name}</strong>? 
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleDeleteRoom}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Rimuovi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkRoomsOpen} onOpenChange={setBulkRoomsOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Configura più camere</DialogTitle>
            <DialogDescription>Crea una sequenza di camere con tipologia, piano e posti uguali.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-2"><Label>Prefisso</Label><Input value={bulkRoomsForm.prefisso} onChange={(e) => setBulkRoomsForm({ ...bulkRoomsForm, prefisso: e.target.value })} placeholder="Es. Camera " /></div>
            <div className="space-y-2"><Label>Numero iniziale</Label><Input type="number" min="0" value={bulkRoomsForm.numero_iniziale} onChange={(e) => setBulkRoomsForm({ ...bulkRoomsForm, numero_iniziale: e.target.value })} /></div>
            <div className="space-y-2"><Label>Numero camere</Label><Input type="number" min="1" max="500" value={bulkRoomsForm.quantita} onChange={(e) => setBulkRoomsForm({ ...bulkRoomsForm, quantita: e.target.value })} /></div>
            <div className="space-y-2"><Label>Tipo</Label><Select value={bulkRoomsForm.tipo} onValueChange={(tipo) => setBulkRoomsForm({ ...bulkRoomsForm, tipo })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Singola">Singola</SelectItem><SelectItem value="Doppia">Doppia</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Piano</Label><Input value={bulkRoomsForm.piano} onChange={(e) => setBulkRoomsForm({ ...bulkRoomsForm, piano: e.target.value })} placeholder="Es. Piano 1" /></div>
            <div className="space-y-2"><Label>Note</Label><Input value={bulkRoomsForm.note} onChange={(e) => setBulkRoomsForm({ ...bulkRoomsForm, note: e.target.value })} /></div>
            <label className="col-span-2 flex items-start gap-2 text-sm text-red-700">
              <input type="checkbox" checked={bulkRoomsForm.sostituisci_configurazione_esistente} onChange={(e) => setBulkRoomsForm({ ...bulkRoomsForm, sostituisci_configurazione_esistente: e.target.checked })} />
              Sostituisci tutte le camere esistenti (possibile solo dopo aver azzerato le assegnazioni).
            </label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setBulkRoomsOpen(false)}>Annulla</Button><Button onClick={handleCreateRoomsBulk} disabled={bulkRoomsSaving} className="bg-emerald-600 hover:bg-emerald-700">{bulkRoomsSaving ? "Configurazione…" : "Conferma"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetAssignmentsOpen} onOpenChange={setResetAssignmentsOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Azzera le assegnazioni camere?</DialogTitle>
            <DialogDescription>Gli ospiti resteranno nei fascicoli, ma verrà rimossa la camera associata e tutti i posti verranno resi disponibili.</DialogDescription>
          </DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setResetAssignmentsOpen(false)}>Annulla</Button><Button onClick={handleResetAssignments} disabled={bulkRoomsSaving} className="bg-red-600 hover:bg-red-700">Azzera assegnazioni</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Lista Lead Compatibili */}
      <Dialog open={leadModalOpen} onOpenChange={setLeadModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserSearch className="w-5 h-5 text-amber-500" />
              Richieste compatibili ({leadCount})
            </DialogTitle>
            <DialogDescription>
              Lead selezionati dal team Casadiriposoperte.it per la tua struttura
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-3">
              {leadCompatibili.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <UserSearch className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Nessun lead compatibile trovato</p>
                </div>
              ) : (
                leadCompatibili.map((lead) => (
                  <Card 
                    key={lead.id} 
                    className="card-base cursor-pointer hover:shadow-md transition-shadow hover:border-amber-300"
                    onClick={() => handleLeadClick(lead)}
                    data-testid={`lead-item-${lead.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-slate-900">{lead.nome_familiare}</p>
                            {lead.stato_interazione && (
                              <Badge variant="outline" className={getStatoBadgeColor(lead.stato_interazione)}>
                                {lead.stato_interazione}
                              </Badge>
                            )}
                          </div>
                          {/* Info Ospite in evidenza */}
                          {(lead.nome_ospite || lead.cognome_ospite) && (
                            <p className="text-sm text-emerald-700 font-medium mb-1">
                              Ospite: {lead.nome_ospite} {lead.cognome_ospite} {lead.eta_ospite ? `(${lead.eta_ospite} anni)` : ''}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                            {lead.citta && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {lead.citta}
                              </span>
                            )}
                            {lead.budget && (
                              <span className="text-emerald-600 font-medium">
                                Budget: {lead.budget}€
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(lead.data_inserimento)}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Sheet Dettaglio Lead */}
      <Sheet open={leadDetailOpen} onOpenChange={setLeadDetailOpen}>
        <SheetContent className="sm:max-w-[480px] overflow-y-auto">
          <SheetHeader className="pb-3">
            <SheetTitle className="flex items-center gap-2 text-lg">
              <UserSearch className="w-5 h-5 text-amber-500" />
              Dettaglio Richiesta
            </SheetTitle>
          </SheetHeader>
          
          {selectedLead && (
            <div className="space-y-5">
              {/* SEZIONE FAMILIARE */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Familiare / Contatto</h4>
                  {selectedLead.stato_interazione && (
                    <Badge variant="outline" className={`${getStatoBadgeColor(selectedLead.stato_interazione)} text-xs`}>
                      {selectedLead.stato_interazione}
                    </Badge>
                  )}
                </div>
                <p className="text-xl font-bold text-slate-900 mb-3">{selectedLead.nome_familiare}</p>
                
                <div className="space-y-2">
                  {selectedLead.telefono && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${selectedLead.telefono}`} className="text-blue-600 hover:underline text-base font-medium">
                        {selectedLead.telefono}
                      </a>
                    </div>
                  )}
                  {selectedLead.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a href={`mailto:${selectedLead.email}`} className="text-blue-600 hover:underline text-sm">
                        {selectedLead.email}
                      </a>
                    </div>
                  )}
                  {(selectedLead.citta || selectedLead.provincia) && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700 text-base">
                        {[selectedLead.citta, selectedLead.provincia].filter(Boolean).join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* SEZIONE OSPITE */}
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <h4 className="text-sm font-semibold text-emerald-600 uppercase tracking-wide mb-3">Dati Ospite</h4>
                
                {(selectedLead.nome_ospite || selectedLead.cognome_ospite) ? (
                  <div className="space-y-3">
                    <p className="text-xl font-bold text-emerald-800">
                      {selectedLead.nome_ospite} {selectedLead.cognome_ospite}
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {selectedLead.sesso_ospite && (
                        <div className="bg-white rounded-lg p-3">
                          <p className="text-xs text-slate-500">Sesso</p>
                          <p className="text-base font-semibold text-slate-800">{selectedLead.sesso_ospite}</p>
                        </div>
                      )}
                      {selectedLead.eta_ospite && (
                        <div className="bg-white rounded-lg p-3">
                          <p className="text-xs text-slate-500">Età</p>
                          <p className="text-base font-semibold text-slate-800">{selectedLead.eta_ospite} anni</p>
                        </div>
                      )}
                      {selectedLead.budget && (
                        <div className="bg-white rounded-lg p-3">
                          <p className="text-xs text-emerald-600">Budget</p>
                          <p className="text-base font-bold text-emerald-700">{selectedLead.budget}€</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 italic">Dati ospite non disponibili</p>
                )}
              </div>
              
              {/* DATA INSERIMENTO */}
              {selectedLead.data_inserimento && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Calendar className="w-4 h-4" />
                  <span>Inserito: {formatDate(selectedLead.data_inserimento)}</span>
                </div>
              )}
              
              {/* NOTE CONTATTO */}
              {selectedLead.note_contatto && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm font-semibold text-amber-800 mb-2">Note Contatto</p>
                  <p className="text-base text-amber-700 whitespace-pre-wrap leading-relaxed">{selectedLead.note_contatto}</p>
                </div>
              )}

              <div className="pt-1">
                <Button
                  type="button"
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                  onClick={() => openRichiestaOspiteForm(selectedLead)}
                  data-testid="lead-invia-richiesta-ospite"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Invia richiesta ospite
                </Button>
                <p className="text-[11px] text-slate-500 mt-1.5 text-center">
                  Apre il modulo con i dati del lead già inseriti nelle note; puoi integrare e inviare.
                </p>
              </div>
              
              {/* 4 Azioni Struttura come quadrati */}
              <div className="border-t pt-5">
                <h4 className="text-sm font-semibold text-slate-500 mb-4 uppercase tracking-wide">Azioni</h4>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant={selectedLead.stato_interazione === "Interessata" ? "default" : "outline"}
                    className={`h-20 flex-col gap-2 text-sm font-medium ${selectedLead.stato_interazione === "Interessata" ? "bg-emerald-500 hover:bg-emerald-600" : "hover:bg-emerald-50 hover:border-emerald-300"}`}
                    onClick={() => handleUpdateStato("Interessata")}
                    disabled={updatingStato || selectedLead.stato_interazione === "Interessata"}
                    data-testid="action-interessata"
                  >
                    {updatingStato ? <Loader2 className="w-6 h-6 animate-spin" /> : <ThumbsUp className="w-6 h-6" />}
                    <span>Interessata</span>
                  </Button>
                  
                  <Button
                    variant={selectedLead.stato_interazione === "Contatto avviato" ? "default" : "outline"}
                    className={`h-20 flex-col gap-2 text-sm font-medium ${selectedLead.stato_interazione === "Contatto avviato" ? "bg-amber-500 hover:bg-amber-600" : "hover:bg-amber-50 hover:border-amber-300"}`}
                    onClick={() => handleUpdateStato("Contatto avviato")}
                    disabled={updatingStato || selectedLead.stato_interazione === "Contatto avviato"}
                    data-testid="action-contattato"
                  >
                    {updatingStato ? <Loader2 className="w-6 h-6 animate-spin" /> : <PhoneCall className="w-6 h-6" />}
                    <span>Contatto avviato</span>
                  </Button>
                  
                  <Button
                    variant={selectedLead.stato_interazione === "Non idoneo" ? "default" : "outline"}
                    className={`h-20 flex-col gap-2 text-sm font-medium ${selectedLead.stato_interazione === "Non idoneo" ? "bg-red-500 hover:bg-red-600" : "hover:bg-red-50 hover:border-red-300"}`}
                    onClick={() => handleUpdateStato("Non idoneo")}
                    disabled={updatingStato || selectedLead.stato_interazione === "Non idoneo"}
                    data-testid="action-non-idoneo"
                  >
                    {updatingStato ? <Loader2 className="w-6 h-6 animate-spin" /> : <ThumbsDown className="w-6 h-6" />}
                    <span>Non idoneo</span>
                  </Button>
                  
                  <Button
                    variant={selectedLead.stato_interazione === "Ingresso confermato" ? "default" : "outline"}
                    className={`h-20 flex-col gap-2 text-sm font-medium ${selectedLead.stato_interazione === "Ingresso confermato" ? "bg-purple-500 hover:bg-purple-600" : "hover:bg-purple-50 hover:border-purple-300"}`}
                    onClick={() => handleUpdateStato("Ingresso confermato")}
                    disabled={updatingStato || selectedLead.stato_interazione === "Ingresso confermato"}
                    data-testid="action-ingresso"
                  >
                    {updatingStato ? <Loader2 className="w-6 h-6 animate-spin" /> : <Home className="w-6 h-6" />}
                    <span>Ingresso confermato</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>


      {/* Modulo Invia richiesta ospite */}
      <Dialog
        open={richiestaOspiteFormOpen}
        onOpenChange={(open) => {
          setRichiestaOspiteFormOpen(open);
          if (!open) setRichiestaLeadContext(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sky-700">
              <Send className="w-5 h-5" />
              Invia richiesta ospite
            </DialogTitle>
            <DialogDescription>
              Compila i campi obbligatori.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {richiestaLeadContext && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Richiesta collegata al lead: <strong>{richiestaLeadContext.nome_familiare || "—"}</strong>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="richiesta-data-ingresso">Data ingresso prevista *</Label>
              <Input
                id="richiesta-data-ingresso"
                type="date"
                value={richiestaForm.data_ingresso_prevista}
                onChange={(e) => setRichiestaForm({ ...richiestaForm, data_ingresso_prevista: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="richiesta-sesso">Sesso *</Label>
              <Select
                value={richiestaForm.sesso || undefined}
                onValueChange={(v) => setRichiestaForm({ ...richiestaForm, sesso: v })}
              >
                <SelectTrigger id="richiesta-sesso">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Uomo">Uomo</SelectItem>
                  <SelectItem value="Donna">Donna</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="richiesta-tipologia">Tipologia *</Label>
              <Select
                value={richiestaForm.tipologia || undefined}
                onValueChange={(v) => setRichiestaForm({ ...richiestaForm, tipologia: v })}
              >
                <SelectTrigger id="richiesta-tipologia">
                  <SelectValue placeholder="Seleziona tipologia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ingresso residenziale (senza RSA)">Ingresso residenziale (senza RSA)</SelectItem>
                  <SelectItem value="Centro diurno">Centro diurno</SelectItem>
                  <SelectItem value="Soggiorno breve">Soggiorno breve</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="richiesta-camera">Camera *</Label>
              <Select
                value={richiestaForm.camera || undefined}
                onValueChange={(v) => setRichiestaForm({ ...richiestaForm, camera: v })}
              >
                <SelectTrigger id="richiesta-camera">
                  <SelectValue placeholder="Seleziona camera" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Singola">Singola</SelectItem>
                  <SelectItem value="Doppia">Doppia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="richiesta-note">Note</Label>
              <Textarea
                id="richiesta-note"
                value={richiestaForm.note}
                onChange={(e) => setRichiestaForm({ ...richiestaForm, note: e.target.value })}
                placeholder="Dettagli utili per il team (diagnosi, urgenza, altre preferenze...)"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setRichiestaOspiteFormOpen(false);
                setRichiestaLeadContext(null);
              }}
            >
              Annulla
            </Button>
            <Button
              type="button"
              className="bg-sky-600 hover:bg-sky-700"
              onClick={submitRichiestaOspite}
              disabled={invioRichiestaLoading}
            >
              {invioRichiestaLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Invia richiesta ospite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={richiestaInviataDialogOpen} onOpenChange={setRichiestaInviataDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-emerald-800">Richiesta inviata</DialogTitle>
            <DialogDescription className="text-slate-600 text-base leading-relaxed">
              Abbiamo registrato la tua richiesta. Puoi controllare lo
              stato in qualsiasi momento con il pulsante «Vedi richieste».
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setRichiestaInviataDialogOpen(false)}>
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={richiesteOspiteDialogOpen} onOpenChange={setRichiesteOspiteDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-sky-600" />
              Le tue richieste ospite
            </DialogTitle>
            <DialogDescription>
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 min-h-[200px] pr-3">
            {loadingRichiesteOspite ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
              </div>
            ) : richiesteOspite.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Nessuna richiesta in elenco.</p>
            ) : (
              <ul className="space-y-3">
                {richiesteOspite.map((r) => (
                  <li
                    key={r.id}
                    className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{r.tipologia}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Ingresso previsto: {r.data_ingresso_prevista || "—"}
                          {r.sesso ? ` · Sesso: ${r.sesso}` : ""}
                          {r.camera ? ` · Camera: ${r.camera}` : ""}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-1">
                          Inviata: {r.created_at ? formatDate(r.created_at) : "—"}
                        </p>
                      </div>
                      <Badge variant="outline" className={getStatoRichiestaOspiteBadge(r.stato)}>
                        {formatStatoRichiestaLabel(r.stato)}
                      </Badge>
                    </div>
                    {r.note ? (
                      <p className="text-xs text-slate-600 mt-2 whitespace-pre-wrap line-clamp-4">{r.note}</p>
                    ) : null}
                    <div className="mt-3 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => nascondiRichiestaOspite(r.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Rimuovi dall&apos;elenco
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRichiesteOspiteDialogOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
